// Wallet page
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Wallet as WalletIcon, ArrowUpRight, ArrowDownRight, Coins, FileDown } from "lucide-react";
import { toast, Toaster } from "sonner";
import { getRates, nxToLocal, formatMoney } from "../lib/currency";

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [withdrawAmt, setWithdrawAmt] = useState(20);
  const [fx, setFx] = useState(null);

  const ccy = user?.display_currency || "AUD";

  const load = () => api.get("/wallet").then(({ data }) => setData(data)).catch(() => setData(null));
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => { getRates().then(setFx); }, []);

  if (!user) return <div className="text-center text-white/40 py-20">Sign in to view wallet.</div>;
  if (!data) return <div className="text-white/40">Loading…</div>;

  const topup = async (amt) => {
    await api.post(`/wallet/topup?amount=${amt}`);
    toast.success(`+${amt} NX added`);
    refresh(); load();
  };

  const withdraw = async () => {
    try {
      const { data: r } = await api.post("/wallet/withdraw", { amount: parseFloat(withdrawAmt), method: "paypal" });
      toast.success(`Withdrawal requested · fee ${r.fee.toFixed(2)} · net ${r.net.toFixed(2)}`);
      refresh(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="surface-light max-w-[1500px] mx-auto space-y-10" data-testid="wallet-page">
      <Toaster theme="dark" position="top-center" />
      <div>
        <div className="label-eyebrow mb-3">// Wallet</div>
        <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">Your NX coins.</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-white/5">
        <div className="bg-nexus-surface p-8">
          <WalletIcon className="w-6 h-6 text-nexus-cyan mb-4" />
          <div className="label-eyebrow">// balance</div>
          <div className="font-display font-black text-5xl mt-3" data-testid="wallet-balance">{data.balance.toFixed(0)}<span className="text-base text-white/40 ml-2">NX</span></div>
          {fx && (
            <div className="text-[11px] text-white/50 mt-1" data-testid="wallet-balance-local">
              ≈ {formatMoney(nxToLocal(data.balance, ccy, fx), ccy, fx)}
              {ccy !== "AUD" && (
                <span className="text-white/30 ml-1">· base AUD {formatMoney(nxToLocal(data.balance, "AUD", fx), "AUD", fx)}</span>
              )}
            </div>
          )}
          <a href="/coins" className="btn-ghost mt-4 !py-2 !px-3 text-[9px] inline-flex">Buy more coins</a>
        </div>
        <div className="bg-nexus-surface p-8">
          <Coins className="w-6 h-6 text-nexus-amber mb-4" />
          <div className="label-eyebrow">// xp · vp</div>
          <div className="font-display font-black text-4xl mt-3">{data.xp}<span className="text-white/30"> / </span>{data.vp}</div>
          <div className="text-[11px] text-white/40 mt-2 tracking-wider">Viewer XP · Vibe Points</div>
        </div>
        <div className="bg-nexus-surface p-8">
          <div className="label-eyebrow mb-4">// quick top-up (mock)</div>
          <div className="grid grid-cols-3 gap-2">
            {[10, 25, 50, 100, 250, 500].map((a) => (
              <button key={a} onClick={() => topup(a)} data-testid={`topup-${a}`} className="text-[11px] tracking-[0.2em] uppercase font-display py-2 border border-white/10 hover:border-nexus-cyan hover:text-nexus-cyan">+{a}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="glass p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow">// withdraw to paypal</div>
            <div className="text-sm text-white/60 mt-2">{user.kyc_verified ? "3% fee (min 0.50 NX). Add your PayPal email in Settings to enable withdrawals." : "⚠ Identity verification required before withdrawing."}</div>
          </div>
          <div className="flex items-center gap-3">
            {user.kyc_verified ? (
              <>
                <input value={withdrawAmt} onChange={(e) => setWithdrawAmt(e.target.value)} type="number" className="w-28 bg-transparent border border-white/10 px-3 py-2" />
                <button onClick={withdraw} className="btn-nexus" data-testid="withdraw-btn">Withdraw</button>
              </>
            ) : (
              <a href="/verify" className="btn-nexus" data-testid="kyc-cta">Verify identity</a>
            )}
          </div>
        </div>
      </div>

      <BountyProgressCard />
      <PayoutHistoryCard />

      <TaxExportPanel />

      <div>
        <div className="label-eyebrow mb-4">// recent tips sent</div>
        <div className="space-y-px bg-white/5">
          {data.tips_sent.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No tips yet.</div>}
          {data.tips_sent.map((t) => (
            <div key={t.id} className="bg-nexus-surface p-4 flex items-center justify-between text-sm">
              <div className="flex items-center gap-3"><ArrowUpRight className="w-4 h-4 text-nexus-crimson" /> Stream tip</div>
              <div className="text-white/40 text-xs">{new Date(t.ts).toLocaleString()}</div>
              <div className="font-mono">-{t.amount.toFixed(0)} NX</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* Tax season export — CSV + JSON download */
function TaxExportPanel() {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/tax/export/${year}`);
      setSummary(data);
    } catch { toast.error("Preview failed"); }
    setBusy(false);
  };

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/tax/export/${year}?format=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `nexus_tax_${year}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded nexus_tax_${year}.csv`);
    } catch { toast.error("Download failed"); }
    setBusy(false);
  };

  return (
    <div className="glass p-6 space-y-4 border-l-2 border-nexus-amber" data-testid="tax-export-panel">
      <div className="flex items-center gap-2">
        <FileDown className="w-4 h-4 text-nexus-amber" />
        <div className="label-eyebrow text-nexus-amber">// tax season export</div>
      </div>
      <p className="text-xs text-white/60 leading-relaxed">
        Download a full annual statement of every NX inflow/outflow on your account — tips, ad revenue, bundle purchases, and PayPal payouts. Hand it to your accountant during tax season.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="number" min={2020} max={new Date().getFullYear()}
          value={year} onChange={(e) => setYear(parseInt(e.target.value || "0", 10))}
          className="w-24 bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="tax-year"
        />
        <button onClick={preview} disabled={busy} className="btn-ghost text-[10px]" data-testid="tax-preview">Preview totals</button>
        <button onClick={downloadCsv} disabled={busy} className="btn-nexus text-[10px]" data-testid="tax-download-csv">
          <FileDown className="w-3 h-3" /> Download CSV
        </button>
      </div>
      {summary && (
        <div className="grid sm:grid-cols-4 gap-px bg-white/5 text-xs" data-testid="tax-summary">
          <div className="bg-nexus-surface p-3"><div className="label-eyebrow">NX in</div><div className="font-display text-xl mt-1 text-nexus-mint">{summary.totals.nx_in.toFixed(2)}</div></div>
          <div className="bg-nexus-surface p-3"><div className="label-eyebrow">NX out</div><div className="font-display text-xl mt-1 text-nexus-crimson">{summary.totals.nx_out.toFixed(2)}</div></div>
          <div className="bg-nexus-surface p-3"><div className="label-eyebrow">AUD spent</div><div className="font-display text-xl mt-1 text-nexus-cyan">${summary.totals.aud_out.toFixed(2)}</div></div>
          <div className="bg-nexus-surface p-3"><div className="label-eyebrow">AUD payouts</div><div className="font-display text-xl mt-1 text-nexus-amber">${summary.totals.aud_in.toFixed(2)}</div></div>
        </div>
      )}
      {summary && summary.row_count === 0 && <div className="text-[11px] text-white/40">No activity for {year}.</div>}
    </div>
  );
}


/* BountyProgressCard — live counter toward the 1,000-impression milestone */
function BountyProgressCard() {
  const [bp, setBp] = useState(null);
  useEffect(() => {
    const load = () => api.get("/me/bounty-progress").then(({ data }) => setBp(data)).catch(() => setBp(null));
    load();
    const t = setInterval(load, 15_000);  // refresh every 15s — feels real-time without thrashing
    return () => clearInterval(t);
  }, []);
  if (!bp) return null;
  const total = bp.total_ad_impressions || 0;
  const inCycle = total % 1000;
  const pct = Math.min(100, (inCycle / 1000) * 100);
  return (
    <div className="glass p-6 space-y-3 border-l-2 border-nexus-mint" data-testid="bounty-progress-card">
      <div className="flex items-center gap-2">
        <Coins className="w-4 h-4 text-nexus-mint" />
        <div className="label-eyebrow text-nexus-mint">// bounty progress · gifted by the owner</div>
      </div>
      <p className="text-xs text-white/60 leading-relaxed">
        Watch ads on streams and reels — every 1,000 impressions you watch earns you a <strong className="text-nexus-mint">+1 NX bounty</strong> gifted from the owner&rsquo;s balance. Non-registered viewers don&rsquo;t count.
      </p>
      <div className="grid sm:grid-cols-4 gap-px bg-white/5 text-xs">
        <div className="bg-nexus-surface p-3" data-testid="bounty-total-impressions">
          <div className="label-eyebrow">total impressions</div>
          <div className="font-display text-2xl mt-1 text-nexus-cyan">{total.toLocaleString()}</div>
        </div>
        <div className="bg-nexus-surface p-3" data-testid="bounty-count">
          <div className="label-eyebrow">bounties earned</div>
          <div className="font-display text-2xl mt-1 text-nexus-mint">{bp.bounty_count || 0}<span className="text-base text-white/40 ml-2">× 1 NX</span></div>
        </div>
        <div className="bg-nexus-surface p-3" data-testid="bounty-received-total">
          <div className="label-eyebrow">received total</div>
          <div className="font-display text-2xl mt-1 text-nexus-amber">{(bp.bounty_received_total_nx || 0).toFixed(0)} <span className="text-base text-white/40">NX</span></div>
        </div>
        <div className="bg-nexus-surface p-3" data-testid="bounty-next-in">
          <div className="label-eyebrow">next bounty in</div>
          <div className="font-display text-2xl mt-1 text-nexus-violet">{bp.impressions_until_next_bounty}<span className="text-base text-white/40"> imp.</span></div>
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[10px] tracking-wider uppercase text-white/40">
          <span>progress to {bp.next_milestone.toLocaleString()}</span>
          <span>{inCycle} / 1,000</span>
        </div>
        <div className="h-2 bg-white/5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-nexus-cyan to-nexus-mint transition-all duration-700" style={{ width: `${pct}%` }} data-testid="bounty-progress-bar" />
        </div>
      </div>
    </div>
  );
}

/* Payout history + lifetime totals — visible to every user */
function PayoutHistoryCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/me/payout-history").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  return (
    <div className="glass p-6 space-y-3 border-l-2 border-nexus-gold" data-testid="payout-history-card">
      <div className="flex items-center gap-2">
        <ArrowUpRight className="w-4 h-4 text-nexus-gold" />
        <div className="label-eyebrow text-nexus-gold">// payouts &amp; withdrawals</div>
      </div>
      <div className="grid sm:grid-cols-4 gap-px bg-white/5 text-xs">
        <div className="bg-nexus-surface p-3" data-testid="po-balance"><div className="label-eyebrow">balance</div><div className="font-display text-2xl mt-1 text-nexus-amber">{(data.wallet_balance || 0).toFixed(0)} <span className="text-sm text-white/40">NX</span></div></div>
        <div className="bg-nexus-surface p-3" data-testid="po-lifetime-nx"><div className="label-eyebrow">lifetime paid out</div><div className="font-display text-2xl mt-1 text-nexus-mint">{data.lifetime_nx_paid_out.toFixed(0)} <span className="text-sm text-white/40">NX</span></div></div>
        <div className="bg-nexus-surface p-3" data-testid="po-lifetime-aud"><div className="label-eyebrow">lifetime AUD</div><div className="font-display text-2xl mt-1 text-nexus-cyan">${data.lifetime_aud_paid_out.toFixed(2)}</div></div>
        <div className="bg-nexus-surface p-3" data-testid="po-count"><div className="label-eyebrow">withdrawals</div><div className="font-display text-2xl mt-1">{data.withdrawals_count}</div></div>
      </div>
      <div className="text-[10px] text-white/40">PayPal on file: <span className="text-white/70">{data.paypal_email || "not set — complete /verify"}</span></div>
      {data.items.length > 0 && (
        <div className="space-y-px bg-white/5 mt-2" data-testid="po-list">
          {data.items.slice(0, 6).map((r) => (
            <div key={r.id} className="bg-nexus-surface p-2.5 flex items-center gap-3 text-xs">
              <div className="flex-1 truncate text-white/70">{r.paypal_email}</div>
              <div className="text-nexus-amber font-mono">{r.nx_amount.toFixed(2)} NX</div>
              <div className="text-nexus-cyan font-mono">${r.aud_amount.toFixed(2)}</div>
              <div className="text-[10px] text-white/40">{new Date(r.ts).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

